import { Card } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TopicMention {
  author: string;
  text: string;
  published_at: string;
}

interface Topic {
  topic: string;
  mentions: number;
  details: TopicMention[];
}

interface TrendingTopicsProps {
  topics: Topic[];
}

export const TrendingTopics = ({ topics }: TrendingTopicsProps) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="h-full bg-zinc-800/20 border-zinc-700">
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 text-white">
          <TrendingUp className="w-5 h-5" />
          Trending Topics
        </h3>
        <div className="space-y-4">
          {topics.length === 0 ? (
            <div className="text-zinc-400">No trending topics yet</div>
          ) : (
            <TooltipProvider>
              {topics.map((topic, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm py-2"
                >
                  <span className="text-zinc-300">{topic.topic}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-violet-400 cursor-default">
                        {topic.mentions} mentions
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[300px]">
                      <div className="space-y-3">
                        <p className="font-medium text-sm">Mentioned by:</p>
                        {topic.details && topic.details.length > 0 ? (
                          topic.details.map((mention, idx) => (
                            <div key={idx} className="text-sm">
                              <p className="text-violet-300">{mention.author}</p>
                              <p className="text-zinc-300 text-xs line-clamp-2 mt-1">
                                "{mention.text.replace(/<[^>]*>/g, '')}"
                              </p>
                              <p className="text-zinc-500 text-xs mt-1">
                                {formatDate(mention.published_at)}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-zinc-400 text-xs">No detailed mentions available</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
            </TooltipProvider>
          )}
        </div>
      </div>
    </Card>
  );
};
